import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useAffiliatePartners } from '@/hooks/useAffiliatePartners';
import { Loader2 } from 'lucide-react';

interface AffiliateCtaInsertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInsert: (partnerId: string, compact: boolean, context?: string) => void;
}

export const AffiliateCtaInsertDialog = ({ open, onOpenChange, onInsert }: AffiliateCtaInsertDialogProps) => {
  const { data: partners, isLoading } = useAffiliatePartners();
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>('');
  const [compact, setCompact] = useState(false);
  const [context, setContext] = useState('');

  const handleInsert = () => {
    if (selectedPartnerId) {
      onInsert(selectedPartnerId, compact, context || undefined);
      setSelectedPartnerId('');
      setContext('');
      setCompact(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Insert Affiliate CTA</DialogTitle>
          <DialogDescription>
            Select an affiliate partner to insert into your blog post
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="partner">Affiliate Partner *</Label>
                <Select value={selectedPartnerId} onValueChange={setSelectedPartnerId}>
                  <SelectTrigger id="partner">
                    <SelectValue placeholder="Select a partner" />
                  </SelectTrigger>
                  <SelectContent>
                    {partners?.map((partner) => (
                      <SelectItem key={partner.id} value={partner.id}>
                        {partner.partner_name} - {partner.category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="context">Context (optional)</Label>
                <input
                  id="context"
                  type="text"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="e.g., Perfect for booking hotels in Tokyo"
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="compact"
                  checked={compact}
                  onCheckedChange={setCompact}
                />
                <Label htmlFor="compact">Compact view</Label>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleInsert} disabled={!selectedPartnerId || isLoading}>
            Insert
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
